import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack, IconButton, Tooltip, Divider, Paper, Typography, TextField } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import TitleIcon from '@mui/icons-material/Title';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import LinkIcon from '@mui/icons-material/Link';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import FormatClearIcon from '@mui/icons-material/FormatClear';

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  label?: string;
  forcePlain?: boolean; // fallback to multiline TextField (useful for Brave issues)
}

const ToolbarButton: React.FC<{ title: string; onClick: () => void; icon: React.ReactNode }> = ({ title, onClick, icon }) => (
  <Tooltip title={title}>
    <IconButton size="small" onClick={onClick}>
      {icon}
    </IconButton>
  </Tooltip>
);

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, placeholder = 'Start typing...', minHeight = 120, label, forcePlain }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isBrave, setIsBrave] = useState<boolean>(false);

  const exec = (cmd: string, val?: string) => {
    try {
      document.execCommand(cmd, false, val);
      // ensure onChange called after command
      if (editorRef.current) {
        onChange(editorRef.current.innerHTML);
      }
    } catch {}
  };

  // Detect Brave and enable plain fallback if necessary
  useEffect(() => {
    let ua = '';
    try { ua = navigator.userAgent || ''; } catch {}
    const vendor = (navigator as any)?.vendor || '';
    const quick = /Brave/i.test(ua) || /Brave Software/i.test(vendor);
    setIsBrave(!!quick);
    // If Brave exposes API
    // @ts-ignore
    if ((navigator as any)?.brave?.isBrave) {
      try {
        // @ts-ignore
        (navigator as any).brave.isBrave().then((res: boolean) => setIsBrave(!!res)).catch(() => {});
      } catch {}
    }
  }, []);

  const setHeading = (level: 1 | 2) => {
    exec('formatBlock', level === 1 ? 'H1' : 'H2');
  };

  const promptLink = () => {
    const url = window.prompt('Enter URL');
    if (!url) return;
    exec('createLink', url);
  };

  const sanitizeHtml = (html: string) => {
    if (!html) return '';
    return html
      // strip bidi control characters including LRM/RLM to avoid odd caret placement
      .replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, '')
      // remove any dir attribute
      .replace(/\sdir=("|')[^"']*\1/gi, '')
      // remove <bdo dir="rtl"> wrappers
      .replace(/<\s*bdo[^>]*dir=("|')rtl\1[^>]*>/gi, '')
      .replace(/<\s*\/\s*bdo\s*>/gi, '')
      // neutralize inline rtl direction and bidi-override enforced by pasted HTML
      .replace(/direction:\s*rtl;?/gi, '')
      // keep plaintext; only strip overrides that can flip order
      .replace(/unicode-bidi:\s*(bidi-override|embed);?/gi, '');
  };

  const [focused, setFocused] = useState(false);

  useEffect(() => {
    // sync external value updates with sanitization
    if (editorRef.current) {
      // Avoid rewriting DOM while user is typing; it breaks caret restoration
      if (focused) {
        // While typing, keep simple LTR and let browser handle paragraphs
        editorRef.current.setAttribute('dir', 'ltr');
        editorRef.current.style.direction = 'ltr';
        (editorRef.current.style as any).unicodeBidi = 'plaintext';
        return;
      }
      const sanitized = sanitizeHtml(value || '');
      if (editorRef.current.innerHTML !== sanitized) {
        editorRef.current.innerHTML = sanitized;
      }
      editorRef.current.setAttribute('dir', 'ltr');
      editorRef.current.style.direction = 'ltr';
      // Non-editing: use plaintext for stable visual order
      (editorRef.current.style as any).unicodeBidi = 'plaintext';
    }
  }, [value, focused]);

  // Removed MutationObserver to avoid feedback loops and potential crashes

  const isEmpty = useMemo(() => !value || value === '<br>' || value.trim() === '', [value]);

  const usePlain = !!forcePlain || isBrave;

  // Plain fallback: use a simple multiline TextField and convert to basic HTML
  if (usePlain) {
    const textValue = (() => {
      // Convert minimal HTML breaks to newlines for display
      try {
        const v = value || '';
        return v.replace(/<br\s*\/?>(?=\n|$)/gi, '\n').replace(/<\/?p>/gi, '').replace(/<div>/gi, '').replace(/<\/div>/gi, '\n');
      } catch { return value || ''; }
    })();

    return (
      <Paper variant="outlined" sx={{ p: 1 }}>
        {label && (
          <Typography variant="caption" color="text.secondary" sx={{ px: 1, pb: 0.5, display: 'block' }}>{label}</Typography>
        )}
        <TextField
          fullWidth
          multiline
          minRows={Math.max(3, Math.floor(minHeight / 24))}
          placeholder={placeholder}
          value={textValue}
          onChange={(e) => {
            const t = e.target.value || '';
            const html = t.replace(/\n/g, '<br>');
            onChange(html);
          }}
          inputProps={{ style: { direction: 'ltr', textAlign: 'left' } }}
        />
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 1 }}>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 1, pb: 0.5, display: 'block' }}>{label}</Typography>
      )}
      <Stack direction="row" spacing={0.5} sx={{ px: 0.5, py: 0.5, flexWrap: 'wrap' }}>
        <ToolbarButton title="Undo" onClick={() => exec('undo')} icon={<UndoIcon fontSize="small" />} />
        <ToolbarButton title="Redo" onClick={() => exec('redo')} icon={<RedoIcon fontSize="small" />} />
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <ToolbarButton title="Bold" onClick={() => exec('bold')} icon={<FormatBoldIcon fontSize="small" />} />
        <ToolbarButton title="Italic" onClick={() => exec('italic')} icon={<FormatItalicIcon fontSize="small" />} />
        <ToolbarButton title="Underline" onClick={() => exec('underline')} icon={<FormatUnderlinedIcon fontSize="small" />} />
        <ToolbarButton title="Clear formatting" onClick={() => exec('removeFormat')} icon={<FormatClearIcon fontSize="small" />} />
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <ToolbarButton title="Heading 1" onClick={() => setHeading(1)} icon={<TitleIcon fontSize="small" />} />
        <ToolbarButton title="Heading 2" onClick={() => setHeading(2)} icon={<LooksTwoIcon fontSize="small" />} />
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <ToolbarButton title="Bulleted list" onClick={() => exec('insertUnorderedList')} icon={<FormatListBulletedIcon fontSize="small" />} />
        <ToolbarButton title="Numbered list" onClick={() => exec('insertOrderedList')} icon={<FormatListNumberedIcon fontSize="small" />} />
        <ToolbarButton title="Insert link" onClick={promptLink} icon={<LinkIcon fontSize="small" />} />
      </Stack>
      <Box
        ref={editorRef}
        role="textbox"
        aria-multiline
        contentEditable
        suppressContentEditableWarning
        dir="ltr"
        translate="no"
        // onBeforeInput removed for stability
        onFocus={() => { setFocused(true); }}
        onBlur={() => setFocused(false)}
        onKeyDown={(e) => {
          // Prevent browser LTR/RTL toggle shortcuts (Ctrl/Cmd + Shift)
          if ((e.ctrlKey || e.metaKey) && e.shiftKey) {
            e.preventDefault();
            // Re-assert LTR
            if (editorRef.current) {
              editorRef.current.setAttribute('dir', 'ltr');
              editorRef.current.style.direction = 'ltr';
            }
          }
        }}
        onInput={(e) => {
          const el = e.target as HTMLElement;
          // Do not rewrite innerHTML during typing; only assert LTR
          el.setAttribute('dir', 'ltr');
          el.style.direction = 'ltr';
          (el.style as any).unicodeBidi = 'plaintext';
          onChange(el.innerHTML);
        }}
        onPaste={(e) => {
          // paste as plain text to avoid hidden direction/bidi attributes
          e.preventDefault();
          const text = e.clipboardData?.getData('text/plain') || '';
          try {
            document.execCommand('insertText', false, text);
          } catch {
            // fallback: append
            if (editorRef.current) {
              const node = document.createTextNode(text);
              editorRef.current.appendChild(node);
            }
          }
          // After paste, sanitize once (safe to rewrite) and keep caret at end
          if (editorRef.current) {
            const sanitized = sanitizeHtml(editorRef.current.innerHTML);
            if (sanitized !== editorRef.current.innerHTML) {
              editorRef.current.innerHTML = sanitized;
              try {
                const selection = window.getSelection();
                if (selection) {
                  selection.removeAllRanges();
                  const range = document.createRange();
                  range.selectNodeContents(editorRef.current);
                  range.collapse(false);
                  selection.addRange(range);
                }
              } catch {}
            }
            onChange(editorRef.current.innerHTML);
          }
        }}
        sx={{
          minHeight,
          borderRadius: 1,
          p: 1.25,
          outline: 'none',
          direction: 'ltr',
          unicodeBidi: 'embed',
          textAlign: 'left',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          '& *': {
            direction: 'ltr !important',
            unicodeBidi: 'embed !important',
            textAlign: 'left !important',
          },
          '&:focus': { boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}33` },
          '&:empty:before': {
            content: 'attr(data-placeholder)',
            color: 'text.disabled',
          },
          '& a': { color: 'primary.main', textDecoration: 'underline' },
        }}
        data-placeholder={placeholder}
        dangerouslySetInnerHTML={{ __html: value || '' }}
      />
    </Paper>
  );
};

export default RichTextEditor;
